import { Checkout } from 'components/checkout'
import { Layout } from 'components/layout'
import { RichTextContainer } from 'components/rich-text-content/rich-text-content.styles'
import {
  AD_FREE_SUBSCRIPTION_PRICES,
  AI_SUBSCRIPTION_PRICES,
  AI_SUBSCRIPTION_TYPES,
  SUBSCRIPTION_PACKAGES,
} from 'constants/ai'
import { PAGES } from 'constants/routes'
import { useLoadInitialData } from 'hooks/database/use-load-initial-data'
import { useAppSelector } from 'hooks/use-app-selector'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useAuthUser } from 'next-firebase-auth'
import { useState } from 'react'
import {
  selectAiSubscriptionIsAdFreeOnly,
  selectAiSubscriptionLoading,
  selectAiSubscriptionType,
  selectIsAiSubscriptionActive,
  selectIsAiSubscriptionCanceled,
  selectIsAiSubscriptionExpired,
  selectIsAiSubscriptionInTrial,
} from 'store/slices/subscription/subscription'
import { Button } from 'ui-kit/button'
import { Divider } from 'ui-kit/divider'
import { urlWithSiteOrigin } from 'utils/url-with-site-origin'

import { FAQs } from './components/faqs'
import {
  FeatureList,
  LoadingSpinner,
  PricingCard,
  PricingFooter,
  PricingGrid,
  PricingHeader,
  PricingPeriod,
  PricingPrice,
  PricingTitle,
  PricingToggle,
  PricingToggleButton,
  PricingToggleContainer,
  SavingsBadge,
  TaxNote,
  TrialBadge,
} from './pricing.styles'
import { faqItems, pricingData } from './resources'
import { getButtonProps, handleAction, PricingPeriodType } from './utils'

export const Pricing = () => {
  useLoadInitialData()
  const authUser = useAuthUser()
  const authLoading = !authUser.clientInitialized
  const uid = authUser.id
  const isLoggedIn = !!uid
  const isSubscriptionActive = useAppSelector(selectIsAiSubscriptionActive)
  const isSubscriptionExpired = useAppSelector(selectIsAiSubscriptionExpired)
  const isSubscriptionCanceled = useAppSelector(selectIsAiSubscriptionCanceled)
  const isSubscriptionInTrial = useAppSelector(selectIsAiSubscriptionInTrial)
  const subscriptionType = useAppSelector(selectAiSubscriptionType)
  const isAdFreeSubscription = useAppSelector(selectAiSubscriptionIsAdFreeOnly)
  const scheduledCancel = useAppSelector((state) => state.subscription.scheduledCancel)

  const isSubscriptionLoading = useAppSelector(selectAiSubscriptionLoading)
  const router = useRouter()
  // Combined loading state for both auth and subscription data
  const isLoading = authLoading || isSubscriptionLoading

  // Check if user has a yearly subscription
  const hasYearlySubscription = isSubscriptionActive && subscriptionType === AI_SUBSCRIPTION_TYPES.YEARLY

  // State to track the selected pricing period
  const [pricingPeriod, setPricingPeriod] = useState<PricingPeriodType>(
    hasYearlySubscription ? PricingPeriodType.YEARLY : PricingPeriodType.MONTHLY
  )

  /**
   * Get pricing for premium plan based on selected period
   * This util is used to get the price and period for the premium plan based on the selected period
   * @param planType - The type of plan to get the price for (premium or ad-free)
   * @returns {price: number, period: string} - The price and period
   */
  const getPremiumPrice = (
    planType: (typeof SUBSCRIPTION_PACKAGES)[keyof typeof SUBSCRIPTION_PACKAGES]
  ): {
    price: number
    period: string
  } => {
    return pricingPeriod === PricingPeriodType.MONTHLY
      ? {
          price:
            planType === SUBSCRIPTION_PACKAGES.PREMIUM
              ? AI_SUBSCRIPTION_PRICES.MONTHLY
              : AD_FREE_SUBSCRIPTION_PRICES.MONTHLY,
          period: 'month',
        }
      : {
          price:
            planType === SUBSCRIPTION_PACKAGES.PREMIUM
              ? AI_SUBSCRIPTION_PRICES.YEARLY
              : AD_FREE_SUBSCRIPTION_PRICES.YEARLY,
          period: 'year',
        }
  }

  /**
   * Calculate savings percentage
   * This util is used to calculate the savings percentage for yearly subscription
   * @param planType - The type of plan to calculate the savings percentage for (premium or ad-free)
   * @returns {number} - The savings percentage
   */
  const calculateSavingsPercentage = (
    planType: (typeof SUBSCRIPTION_PACKAGES)[keyof typeof SUBSCRIPTION_PACKAGES]
  ): number => {
    const monthly =
      planType === SUBSCRIPTION_PACKAGES.PREMIUM ? AI_SUBSCRIPTION_PRICES.MONTHLY : AD_FREE_SUBSCRIPTION_PRICES.MONTHLY
    const yearly =
      planType === SUBSCRIPTION_PACKAGES.PREMIUM ? AI_SUBSCRIPTION_PRICES.YEARLY : AD_FREE_SUBSCRIPTION_PRICES.YEARLY

    const yearlyIfMonthly = monthly * 12
    const savings = yearlyIfMonthly - yearly
    const savingsPercentage = (savings / yearlyIfMonthly) * 100
    return parseInt(savingsPercentage.toFixed(0))
  }

  return (
    <Layout
      authLoading={authLoading}
      uid={uid}
      signOut={authUser.signOut}
      seoProps={{
        title: 'Pricing - TypingMentor Subscription Plans',
        description:
          'Choose between our Ad-free and Premium subscription plans. Remove distractions with our Ad-free plan or get the full experience with AI-powered coaching, personalized feedback, and more.',
        canonicalUrl: urlWithSiteOrigin('/pricing/'),
        pageUrl: urlWithSiteOrigin('/pricing/'),
      }}
    >
      <RichTextContainer compact>
        <h1>Choose Your Typing Mentor Plan</h1>
        <Divider wrapperThickness="2rem" noBar />
        <p>
          Select the plan that best fits your needs. Our Ad-free plan removes distractions so you can focus on practice,
          while our Premium plan includes everything you need to accelerate your typing journey with AI-powered coaching
          and personalized feedback.
        </p>

        {/* Pricing toggle for monthly/yearly */}
        <PricingToggleContainer>
          <PricingToggle>
            <PricingToggleButton
              active={pricingPeriod === PricingPeriodType.MONTHLY}
              onClick={() => setPricingPeriod(PricingPeriodType.MONTHLY)}
            >
              {pricingPeriod === PricingPeriodType.MONTHLY && <span style={{ marginRight: '0.5rem' }}>✓</span>}
              Monthly
            </PricingToggleButton>
            <PricingToggleButton
              active={pricingPeriod === PricingPeriodType.YEARLY}
              onClick={() => setPricingPeriod(PricingPeriodType.YEARLY)}
            >
              {pricingPeriod === PricingPeriodType.YEARLY && <span style={{ marginRight: '0.5rem' }}>✓</span>}
              Yearly
            </PricingToggleButton>
          </PricingToggle>
        </PricingToggleContainer>

        <PricingGrid>
          {pricingData.plans.map(({ id, name, features }) => {
            // Get the plan type (premium or ad-free)
            const planType = name === 'Ad-free' ? SUBSCRIPTION_PACKAGES.AD_FREE : SUBSCRIPTION_PACKAGES.PREMIUM
            // Get the price and period for the plan
            const { price, period } = getPremiumPrice(planType)
            return (
              <PricingCard key={id} style={{ cursor: 'pointer', position: 'relative' }}>
                {pricingPeriod === PricingPeriodType.YEARLY && (
                  <SavingsBadge>Save {calculateSavingsPercentage(planType)}%</SavingsBadge>
                )}
                <PricingHeader>
                  <PricingTitle>{period === 'month' ? `Monthly ${name}` : `Yearly ${name}`}</PricingTitle>
                  <TrialBadge>🎁 7-Day Free Trial</TrialBadge>
                  <PricingPrice>
                    {`$${price}`}
                    <PricingPeriod>/{period}</PricingPeriod>
                  </PricingPrice>
                  <TaxNote>Prices exclude taxes (added at checkout)</TaxNote>
                </PricingHeader>
                <FeatureList>
                  {features.map((feature, index) => (
                    <li key={`${id}-feature-${index}`}>{feature}</li>
                  ))}
                </FeatureList>
                <PricingFooter>
                  <Checkout>
                    {(handleCheckout) => {
                      const {
                        buttonText,
                        actionType,
                        disabled,
                        isLoading: buttonLoading,
                      } = getButtonProps(
                        pricingPeriod,
                        isLoading,
                        isLoggedIn,
                        isSubscriptionActive,
                        isSubscriptionExpired,
                        isSubscriptionCanceled,
                        isSubscriptionInTrial,
                        isAdFreeSubscription ?? false,
                        planType,
                        subscriptionType?.toString(),
                        scheduledCancel?.effectiveAt ?? null
                      )
                      return (
                        <Button
                          title={disabled ? 'Cannot take action while in trial' : ''}
                          onClick={() => {
                            if (!disabled && !buttonLoading) {
                              const navigateTo = handleAction(
                                actionType,
                                pricingPeriod,
                                isLoading,
                                isLoggedIn,
                                planType,
                                handleCheckout
                              )
                              if (navigateTo) {
                                router.push(navigateTo)
                              }
                            }
                          }}
                          fluid
                          disabled={disabled || buttonLoading}
                        >
                          {buttonLoading ? (
                            <>
                              <LoadingSpinner />
                              Loading...
                            </>
                          ) : (
                            buttonText
                          )}
                        </Button>
                      )
                    }}
                  </Checkout>
                </PricingFooter>
              </PricingCard>
            )
          })}
        </PricingGrid>

        <p style={{ textAlign: 'center', marginTop: '2rem' }}>
          Need Help?
          <Link title="Go to contact us page" style={{ marginLeft: '10px' }} href={PAGES['/contact-us/']}>
            Contact Us
          </Link>
        </p>

        <Divider wrapperThickness="2rem" />

        <h2>Why Choose a Subscription?</h2>
        <p>
          While our Free plan provides essential typing practice tools, our subscription plans offer enhanced features
          that can significantly improve your learning experience and typing performance. Try our premium features
          risk-free with our 7-day trial - cancel at any time.
        </p>

        <h2>Key Benefits</h2>
        <ul>
          {pricingData.keyBenefits.map((benefit, index) => (
            <li key={`benefit-${index}`}>
              <strong>{benefit.title}:</strong> {benefit.description}
            </li>
          ))}
        </ul>

        <Divider wrapperThickness="2rem" />

        <FAQs items={faqItems} />
      </RichTextContainer>
    </Layout>
  )
}
